import { Badge, type BadgeTone } from './Badge'
import { hu } from '@/lib/i18n/hu'
import type {
  CountryType,
  MatchingStatus,
  PaymentStatus,
  Source,
  WorkflowStatus,
} from '@/lib/api/types'

const WORKFLOW_TONE: Record<WorkflowStatus, BadgeTone> = {
  received: 'neutral',
  waiting_for_nav: 'info',
  matched: 'ok',
  discrepancy: 'warn',
  under_review: 'warn',
  approval_pending: 'info',
  approved: 'ok',
  handed_to_accountant: 'brand',
  booked: 'ok',
  payment_pending: 'warn',
  paid: 'ok',
  disputed: 'error',
  rejected: 'error',
  archived: 'neutral',
}

export function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  return <Badge tone={WORKFLOW_TONE[status]}>{hu.invoiceStatus[status]}</Badge>
}

const MATCHING_TONE: Record<MatchingStatus, BadgeTone> = {
  pending: 'neutral',
  matched: 'ok',
  nav_only: 'warn',
  doc_only: 'warn',
  foreign_no_nav: 'info',
  amount_mismatch: 'warn',
  number_mismatch: 'warn',
  tax_id_mismatch: 'warn',
  manual_review: 'warn',
  duplicate_suspect: 'warn',
  modification_or_cancel: 'info',
}

export function MatchingStatusBadge({ status }: { status: MatchingStatus }) {
  return <Badge tone={MATCHING_TONE[status]}>{hu.matchingStatus[status]}</Badge>
}

const PAYMENT_TONE: Record<PaymentStatus, BadgeTone> = {
  unpaid: 'neutral',
  partially_paid: 'warn',
  paid: 'ok',
  overdue: 'error',
  not_applicable: 'neutral',
}

export function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  return <Badge tone={PAYMENT_TONE[status]}>{hu.paymentStatus[status]}</Badge>
}

export function SourceBadge({ source }: { source: Source }) {
  const tone: BadgeTone = source === 'nav' ? 'brand' : source === 'email' ? 'info' : 'neutral'
  return <Badge tone={tone}>{hu.source[source]}</Badge>
}

export function CountryBadge({ type }: { type: CountryType }) {
  const tone: BadgeTone = type === 'HU' ? 'neutral' : type === 'EU' ? 'info' : type === 'THIRD_COUNTRY' ? 'warn' : 'neutral'
  return <Badge tone={tone}>{hu.countryType[type]}</Badge>
}

export function MatchingScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <Badge tone="neutral">—</Badge>
  const tone: BadgeTone = score >= 90 ? 'ok' : score >= 70 ? 'warn' : 'error'
  return <Badge tone={tone}>{score.toFixed(0)} / 100</Badge>
}
